import { useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CATEGORIES_CONVERSATION,
  CANAUX_CONVERSATION,
  ETATS_CONVERSATION,
  ETATS_MESSAGE,
  ETATS_PIECE_JOINTE,
  VUES_BOITE,
  calculerDelais,
  AssistantConversation,
  AssistantMessage,
} from '@artosera/shared';
import { api, requeteMultipart, requeteTelechargement, type AppelApi } from './client.js';
import { appelDeRequete, cleMetier, dansLaPortee } from './cles.js';
import { useMutation } from './mutation.js';
export type Categorie = (typeof CATEGORIES_CONVERSATION)[number];
export type VueBoite = (typeof VUES_BOITE)[number];
export interface Conversation {
  id: string;
  canal: (typeof CANAUX_CONVERSATION)[number];
  sujet: string;
  correspondant: { adresse: string; nom: string };
  contactId: string | null;
  oeuvreIds: string[];
  demandeId: string | null;
  etat: (typeof ETATS_CONVERSATION)[number];
  enAttenteJusquau: string | null;
  automatique: boolean;
  categorie: Categorie | null;
  etiquettes: string[];
  assigneeA: string | null;
  nonLue: boolean;
  dernierEntrantLe: string | null;
  dernierMessageLe: string;
  nombreMessages: number;
  contactEfface: boolean;
  version: number;
  extrait: string;
  enRetard: boolean;
  assistant?: Omit<AssistantConversation, 'trieLe'> & { trieLe: string };
  sensible?: boolean;
  motifsSensibles?: string[];
  categorieSource?: 'assistant' | 'humain' | null;
}
export interface Message {
  id: string;
  conversationId: string;
  sens: 'entrant' | 'sortant' | 'note';
  auteurType?: 'COMPTE' | 'SYSTEME';
  assistant?: Omit<AssistantMessage, 'prepareLe' | 'textePrepare'> & { prepareLe: string };
  etat: (typeof ETATS_MESSAGE)[number];
  de: { adresse: string; nom: string };
  texte: string;
  creeLe: string;
  erreurEnvoi: string | null;
  htmlDisponible: boolean;
  tronque: boolean;
  piecesJointes: { id: string; nom: string; etat: (typeof ETATS_PIECE_JOINTE)[number] }[];
  suivi: { ouvertures: number; premiereOuvertureLe: string | null } | null;
}
export interface FiltresBoite {
  vue?: VueBoite;
  q?: string;
  canal?: string;
  categorie?: string;
  etiquette?: string;
  assigneeA?: string;
  contactId?: string;
  oeuvreId?: string;
  page?: number;
  urgentes?: boolean;
}
export interface ReglagesBoite {
  version: number;
  adresseReponse: 'reception' | 'galerie';
  signature: string;
  etiquettes: string[];
  objectifsHeures: Record<Categorie, number>;
  suiviOuverture: boolean;
  expediteursIgnores: string[];
  adresseReception: string | null;
  receptionDisponible: boolean;
  dernierRecuLe: string | null;
}
export interface PreferencesBoite {
  resumeQuotidien: boolean;
  alertes: boolean;
}
export interface ListeConversations {
  conversations: Conversation[];
  pagination: { page: number; pages: number; total: number };
}
const base = (g: string) => `/g/${encodeURIComponent(g)}/boite-reception`;
export const boiteApi = {
  lister(g: string, filtres: FiltresBoite, appel?: AppelApi) {
    const params = new URLSearchParams();
    for (const [cle, valeur] of Object.entries(filtres))
      if (valeur !== undefined && valeur !== '') params.set(cle, String(valeur));
    return api.get<ListeConversations>(`${base(g)}/conversations?${params}`, {
      galerie: g,
      ...appel,
    });
  },
  lire: (g: string, id: string, appel?: AppelApi) =>
    api.get<{ conversation: Conversation; messages: Message[] }>(
      `${base(g)}/conversations/${encodeURIComponent(id)}`,
      { galerie: g, ...appel },
    ),
  compteurs: (g: string, appel?: AppelApi) =>
    api.get<{ aTraiter: number; nonLues: number; aMoi: number }>(`${base(g)}/compteurs`, {
      galerie: g,
      ...appel,
    }),
  reglages: (g: string, appel?: AppelApi) =>
    api.get<{ reglages: ReglagesBoite }>(`${base(g)}/reglages`, { galerie: g, ...appel }),
  preferences: (g: string, appel?: AppelApi) =>
    api.get<{ preferences: PreferencesBoite }>(`${base(g)}/preferences`, { galerie: g, ...appel }),
  delais: (g: string, jours: number, appel?: AppelApi) =>
    api.get<ReturnType<typeof calculerDelais>>(`${base(g)}/delais?jours=${jours}`, {
      galerie: g,
      ...appel,
    }),
  modifier: (g: string, id: string, corps: object, appel?: AppelApi) =>
    api.patch<{ conversation: Conversation }>(
      `${base(g)}/conversations/${encodeURIComponent(id)}`,
      corps,
      { galerie: g, ...appel },
    ),
  geste: (g: string, chemin: string, corps: object, appel?: AppelApi) =>
    api.post<{ conversation?: Conversation; message?: Message }>(`${base(g)}${chemin}`, corps, {
      galerie: g,
      ...appel,
    }),
  regler(g: string, r: ReglagesBoite, appel?: AppelApi) {
    const {
      version,
      adresseReponse,
      signature,
      etiquettes,
      objectifsHeures,
      suiviOuverture,
      expediteursIgnores,
    } = r;
    return api.put(
      `${base(g)}/reglages`,
      {
        version,
        adresseReponse,
        signature,
        etiquettes,
        objectifsHeures,
        suiviOuverture,
        expediteursIgnores,
      },
      { galerie: g, ...appel },
    );
  },
  preferer: (g: string, corps: PreferencesBoite, appel?: AppelApi) =>
    api.put(`${base(g)}/preferences`, corps, { galerie: g, ...appel }),
  composer(
    g: string,
    chemin: string,
    corps: Record<string, string | number>,
    fichiers: File[],
    appel?: AppelApi,
  ) {
    const donnees = new FormData();
    for (const [cle, valeur] of Object.entries(corps)) donnees.set(cle, String(valeur));
    for (const fichier of fichiers) donnees.append('pieces', fichier);
    return requeteMultipart<{ message: Message }>(`${base(g)}${chemin}`, donnees, {
      galerie: g,
      ...appel,
    });
  },
  async html(g: string, id: string, appel?: AppelApi) {
    const fichier = await requeteTelechargement(
      `${base(g)}/messages/${encodeURIComponent(id)}/html`,
      { galerie: g, ...appel },
    );
    return fichier.blob.text();
  },
  piece: (g: string, message: string, piece: string, appel?: AppelApi) =>
    requeteTelechargement(
      `${base(g)}/pieces/${encodeURIComponent(message)}/${encodeURIComponent(piece)}`,
      { galerie: g, ...appel },
    ),
};
export function useConversations(g: string, filtres: FiltresBoite, enabled = true) {
  return useQuery({
    queryKey: cleMetier(g, 'boite-reception', 'liste', filtres),
    queryFn: (c) => boiteApi.lister(g, filtres, appelDeRequete(c)),
    enabled,
  });
}
export function useConversation(g: string, id: string) {
  return useQuery({
    queryKey: cleMetier(g, 'boite-reception', 'conversation', id),
    queryFn: (c) => boiteApi.lire(g, id, appelDeRequete(c)),
  });
}
export function useReglagesBoite(g: string) {
  return useQuery({
    queryKey: cleMetier(g, 'boite-reception', 'reglages'),
    queryFn: (c) => boiteApi.reglages(g, appelDeRequete(c)),
  });
}
export function useCompteursBoite(g: string, enabled: boolean) {
  return useQuery({
    queryKey: cleMetier(g, 'boite-reception', 'compteurs'),
    queryFn: (c) => boiteApi.compteurs(g, appelDeRequete(c)),
    enabled,
    refetchInterval: 60_000,
    staleTime: 30_000,
  });
}
export function useActionBoite<T, V>(g: string, action: (corps: V, appel: AppelApi) => Promise<T>) {
  const client = useQueryClient();
  return useMutation(
    dansLaPortee({
      mutationFn: action,
      onSettled: () => client.invalidateQueries({ queryKey: cleMetier(g, 'boite-reception') }),
    }),
  );
}
